// src/components/Invoice/InvoiceDetail.tsx
import React, { useEffect, useState } from "react";
import { getReturnedItemsByInvoiceNumber } from "../../firebase/returns/getReturnedItemsByInvoiceNumber";
import ReturnItemRecord from "./ReturnItemRecord";
import { auditedSaveReturnItem } from "../../firebase/auditLog/wrappedOperations";
import PaymentRecord from "./PaymentRecord";
import { getPaymentsByInvoiceNumber } from "../../firebase/payment/getPaymentsByInvoiceNumber";
import { InvoiceData, LineItem } from "../../models/invoice";
import { Box, Typography, Divider, Paper, Grid, Button } from "@mui/material";
import { getInvoiceByNumber } from "../../firebase/invoice/getInvoiceByNumber";
import { PaymentData } from "../../models/payment";
import { formatDate } from "../../utils/calculations";
import { savePayment } from "../../firebase/payment/savePayment";
import { ReturnItemData } from "../../models/credit";
import { getItemById } from "../../firebase/itemsAndInventory/getItemById";
import { updateItemAvailableQuantity } from "../../firebase/itemsAndInventory/updateItemAvailableQuantity";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { fetchItems } from "../../redux/slices/itemsSlice";
import { useUserSettings } from "../../context/UserSettingsContext";

interface ReturnedInvoicedItem extends ReturnItemData {
	itemCode: string;
}

// Helper to send invoice details via WhatsApp (calls backend API)
const sendInvoiceOnWhatsApp = async (
	invoice: InvoiceData,
	mobileNumber: string,
	TotalAmount: number,
	RemainingAmount: number,
	PaidAmount: number
) => {
	if (!mobileNumber) {
		alert("No mobile number found for this invoice.");
		return;
	}
	try {
		const WhatsAppAPI_URL = "http://localhost:5001/api/send-whatsapp"; // Update with your backend API URL
		// Ensure number is in WhatsApp format: country code + number + @c.us
		let waNumber = mobileNumber;
		if (!/^[0-9]+@c\.us$/.test(mobileNumber)) {
			// Remove non-digits, prepend country code if missing
			waNumber = mobileNumber.replace(/\D/g, "");
			if (!waNumber.startsWith("91")) waNumber = "91" + waNumber;
			waNumber = waNumber + "@c.us";
		}
		//`Invoice #${invoice.invoiceNumber}\nCustomer: ${invoice.customerName}\nTotal: ₹${invoice.items.reduce((sum, item) => sum + item.sellPrice * item.quantity, 0).toFixed(2)}`
		const response = await fetch(WhatsAppAPI_URL, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				number: waNumber,
				message: `Pradyumn Garments (An Outlet Of FashionBazi Pvt. Ltd.) \nInvoice #${invoice.invoiceNumber}\nCustomer: ${invoice.customerName}\nTotal: ₹${TotalAmount.toFixed(2)}\nPaid: ₹${PaidAmount.toFixed(2)}\nRemaining: ₹${RemainingAmount.toFixed(2)} \n Thank you for shopping with us! \n Rate us on Google: https://share.google/wO41rWQON0LN9UPzZ`
			})
		});
		if (response.ok) {
			alert("Invoice sent on WhatsApp!");
		} else {
			const data = await response.json().catch(() => ({}));
			alert(
				"Failed to send invoice on WhatsApp. " +
					(data.error ? `Error: ${data.error}` : "")
			);
		}
	} catch (err: any) {
		alert("Failed to send invoice on WhatsApp. " + (err?.message || err));
	}
};

const calculateProfitPercent = (purchase: number, sell: number) => {
	if (purchase === 0) return "0.00";
	return (((sell - purchase) / purchase) * 100).toFixed(2);
};

export const InvoiceDetail = () => {
	// get invoiceNumber from query param
	const urlParams = new URLSearchParams(window.location.search);
	const invoiceNumber = urlParams.get("invoiceNumber");

	const [invoice, setInvoice] = useState<InvoiceData | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [payments, setPayments] = useState<PaymentData[]>([]);
	const [isPaymentsLoading, setIsPaymentsLoading] = useState(false);
	const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
	const [returnDialogOpen, setReturnDialogOpen] = useState(false);
	const [returnedItems, setReturnedItems] = useState<ReturnItemData[]>([]);
	const [isReturnedLoading, setIsReturnedLoading] = useState(false);
	const dispatch = useDispatch<AppDispatch>();
	const { maskProfit } = useUserSettings();

	const handleClose = () => {
		window.close();
	};

	useEffect(() => {
		const fetch = async (invNum: string) => {
			const data = await getInvoiceByNumber(invNum);
			if (data) {
				setInvoice(data);
			}
			setIsFetching(false);
		};
		if (invoiceNumber) {
			setIsFetching(true);
			fetch(invoiceNumber);
		}
	}, []);

	useEffect(() => {
		if (!invoiceNumber) return;
		setIsReturnedLoading(true);
		getReturnedItemsByInvoiceNumber(invoiceNumber)
			.then((data) => setReturnedItems(data))
			.finally(() => setIsReturnedLoading(false));
	}, [invoiceNumber, returnDialogOpen]);

	useEffect(() => {
		if (!invoiceNumber) return;
		setIsPaymentsLoading(true);
		getPaymentsByInvoiceNumber(invoiceNumber)
			.then((data) => setPayments(data))
			.finally(() => setIsPaymentsLoading(false));
	}, [invoiceNumber]);

	const totalPurchase = invoice
		? invoice.items.reduce(
				(sum, item) => sum + item.purchasePrice * item.quantity,
				0
			)
		: 0;
	const totalSell = invoice
		? invoice.items.reduce(
				(sum, item) => sum + item.sellPrice * item.quantity,
				0
			)
		: 0;
	const grossProfitPercent =
		totalPurchase === 0
			? "0.00"
			: (((totalSell - totalPurchase) / totalPurchase) * 100).toFixed(2);
	const grossProfitAmount = (totalSell - totalPurchase).toFixed(2);

	const totalPayments = payments.reduce(
		(sum, p) => sum + Number(p.amount),
		0
	);

	const remainingItems: LineItem[] = invoice
		? invoice.items
				.map((item) => {
					const returnedQty = returnedItems
						.filter((ri) => ri.lineItemId === item.lineItemId)
						.reduce((sum, ri) => sum + ri.quantity, 0);
					return {
						...item,
						quantity: item.quantity - returnedQty
					};
				})
				.filter((item) => item.quantity > 0)
		: [];

	const totalAmountOfReturnedItems = Number(
		returnedItems
			.reduce((sum, item) => {
				const returnedQty = returnedItems
					.filter((ri) => ri.lineItemId === item.lineItemId)
					.reduce((s, ri) => s + ri.quantity, 0);
				return sum + returnedQty * item.amount;
			}, 0)
			.toFixed(2)
	);

	const canRecordPayment =
		invoice && totalPayments < totalSell - totalAmountOfReturnedItems;

	const remainingPayment = Number(
		(totalSell - (totalPayments + totalAmountOfReturnedItems)).toFixed(2)
	);

	const returnedItemsWithCode: ReturnedInvoicedItem[] = returnedItems.map(
		(ri) => {
			const originalItem = invoice?.items.find(
				(item) => item.lineItemId === ri.lineItemId
			);
			return {
				...ri,
				itemCode: originalItem?.itemCode || ""
			};
		}
	);

	const getItemKeyByLineItemId = (lineItemId: string): string | null => {
		const lineItem = invoice?.items.find(
			(item) => item.lineItemId === lineItemId
		);
		return lineItem ? lineItem.itemKey : null;
	};

	return isFetching ? (
		<div>Fetching invoice details...</div>
	) : (
		<Paper elevation={3} sx={{ padding: 3, marginTop: 2 }}>
			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", sm: "center" },
					marginBottom: 2,
					gap: 2
				}}
			>
				<Typography
					variant="h5"
					gutterBottom
					sx={{ mb: { xs: 0, sm: "0.35em" } }}
				>
					Invoice Details
				</Typography>
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						gap: 2,
						width: { xs: "100%", sm: "auto" }
					}}
				>
					{invoice && invoice.mobileNumber && (
						<Button
							type="button"
							variant="outlined"
							color="success"
							sx={{ width: { xs: "100%", sm: "auto" } }}
							onClick={(e) => {
								if (e && e.preventDefault) e.preventDefault();
								sendInvoiceOnWhatsApp(
									invoice,
									invoice.mobileNumber,
									totalSell,
									remainingPayment,
									totalPayments
								);
							}}
						>
							Send on WhatsApp
						</Button>
					)}
					<Button
						variant="contained"
						color="secondary"
						onClick={handleClose}
						sx={{ width: { xs: "100%", sm: "auto" } }}
					>
						Close
					</Button>
				</Box>
			</Box>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, sm: 6 }}>
					<Typography variant="body2">
						<strong>Invoice Number:</strong>{" "}
						{invoice?.invoiceNumber}
					</Typography>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<Typography variant="body2">
						<strong>Date:</strong>{" "}
						{invoice?.createdAt
							? formatDate(invoice.createdAt)
							: "—"}
					</Typography>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<Typography variant="body2">
						<strong>Customer:</strong>{" "}
						{invoice?.customerName || "—"}
					</Typography>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<Typography variant="body2">
						<strong>Mobile:</strong> {invoice?.mobileNumber || "—"}
					</Typography>
				</Grid>
			</Grid>
			<Divider sx={{ marginY: 2 }} />
			<Typography variant="h6" gutterBottom>
				Line Items
			</Typography>
			<div className="table-responsive">
				<table className="table table-bordered table-striped">
					<thead className="table-light">
						<tr>
							<th>Description</th>
							<th>Purchase Price</th>
							<th>Sell Price</th>
							<th>Quantity</th>
							<th>Line Total</th>
							<th>Profit Amount</th>
							<th>Profit %</th>
						</tr>
					</thead>
					<tbody>
						{invoice?.items.map((item: LineItem, index: number) => {
							const lineTotal = item.sellPrice * item.quantity;
							const profitAmount =
								lineTotal - item.purchasePrice * item.quantity;
							return (
								<tr key={index}>
									<td>{`(${item.itemCode}) ${item.description}`}</td>
									<td>
										{maskProfit
											? "••••••"
											: `₹${item.purchasePrice.toFixed(2)}`}
									</td>
									<td>₹{item.sellPrice.toFixed(2)}</td>
									<td>{item.quantity}</td>
									<td>₹{lineTotal.toFixed(2)}</td>
									<td>
										{maskProfit
											? "••••••"
											: `₹${profitAmount.toFixed(2)}`}
									</td>
									<td>
										{maskProfit
											? "••••••"
											: `${calculateProfitPercent(item.purchasePrice, item.sellPrice)}%`}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<Divider sx={{ marginY: 2 }} />
			<Box sx={{ fontWeight: "bold" }}>
				<Typography>
					Total Purchase:{" "}
					{maskProfit ? "••••••" : `₹${totalPurchase.toFixed(2)}`}
				</Typography>
				<Typography>Total Sell: ₹{totalSell.toFixed(2)}</Typography>
				<Typography>
					Gross Profit:{" "}
					{maskProfit ? "••••••" : `₹${grossProfitAmount}`}
					{maskProfit ? "" : ` (${grossProfitPercent}%)`}
				</Typography>
			</Box>
			<Divider sx={{ marginY: 2 }} />

			<Typography variant="h6" gutterBottom>
				Payment Records
			</Typography>
			{canRecordPayment && (
				<Box sx={{ mb: 2 }}>
					<Typography color="warning.main" sx={{ mb: 1 }}>
						Remaining amount to be paid: ₹{" "}
						{remainingPayment.toFixed(2)}
					</Typography>
					<Button
						variant="contained"
						color="primary"
						sx={{ width: { xs: "100%", sm: "auto" } }}
						onClick={() => setPaymentDialogOpen(true)}
					>
						Record Payment
					</Button>
					{paymentDialogOpen && (
						<PaymentRecord
							invoiceNumber={invoice.invoiceNumber}
							open={paymentDialogOpen}
							onClose={() => setPaymentDialogOpen(false)}
							onSave={async (payment: PaymentData) => {
								const amt = Number(payment.amount);
								if (amt < 0) {
									alert("Payment can't be negative.");
									return;
								}
								if (amt > remainingPayment) {
									alert(
										`Payment cannot be greater than remaining amount (₹${remainingPayment.toFixed(
											2
										)}).`
									);
									return;
								}
								await savePayment(payment);
								// Refresh payments after saving
								setIsPaymentsLoading(true);
								getPaymentsByInvoiceNumber(
									invoice.invoiceNumber
								)
									.then((data) => setPayments(data))
									.finally(() => setIsPaymentsLoading(false));
							}}
							defaultAmount={remainingPayment}
						/>
					)}
				</Box>
			)}
			{isPaymentsLoading ? (
				<div>Loading payments...</div>
			) : payments.length === 0 ? (
				<Typography>No payments recorded for this invoice.</Typography>
			) : (
				<div className="table-responsive">
					<table className="table table-bordered table-striped">
						<thead className="table-light">
							<tr>
								<th>Amount</th>
								<th>Mode</th>
								<th>Date</th>
							</tr>
						</thead>
						<tbody>
							{payments.map((payment, idx) => {
								return (
									<tr key={idx}>
										<td>
											₹{Number(payment.amount).toFixed(2)}
										</td>
										<td>{payment.mode}</td>
										<td>
											{formatDate(payment.paymentDate)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			{/* Return Items Button and Modal */}
			{invoice && remainingItems.length > 0 && (
				<Box sx={{ mt: 2, mb: 2 }}>
					<Button
						variant="outlined"
						color="secondary"
						sx={{ width: { xs: "100%", sm: "auto" } }}
						onClick={() => setReturnDialogOpen(true)}
					>
						Return Item
					</Button>
					{returnDialogOpen && (
						<ReturnItemRecord
							invoiceNumber={invoice.invoiceNumber}
							items={remainingItems}
							open={returnDialogOpen}
							onClose={() => setReturnDialogOpen(false)}
							onSave={async (data: ReturnItemData) => {
								const itemKey =
									getItemKeyByLineItemId(data.lineItemId) ??
									"";
								const inventoryItem =
									await getItemById(itemKey);
								if (!inventoryItem) {
									alert(
										"Selected item not found in inventory."
									);
									return;
								}
								// Save return item with audit logging
								await auditedSaveReturnItem(
									data,
									inventoryItem
								);

								// update items available quantity in inventory
								if (inventoryItem) {
									const newAvailableQuantity =
										(inventoryItem.availableQuantity || 0) +
										data.quantity;
									await updateItemAvailableQuantity(
										itemKey,
										newAvailableQuantity
									);
								}
								// Refresh items in Redux store after Returning item
								dispatch(fetchItems());
								setReturnDialogOpen(false);
								//  refresh credits
								setIsReturnedLoading(true);
								getReturnedItemsByInvoiceNumber(
									invoice.invoiceNumber
								)
									.then((data) => setReturnedItems(data))
									.finally(() => setIsReturnedLoading(false));
							}}
						/>
					)}
				</Box>
			)}
			{/* Returned Items Table */}
			<Typography variant="h6" gutterBottom>
				Returned Items
			</Typography>
			{isReturnedLoading ? (
				<div>Loading returned items...</div>
			) : returnedItems.length === 0 ? (
				<Typography>No items returned for this invoice.</Typography>
			) : (
				<div className="table-responsive">
					<table className="table table-bordered table-striped">
						<thead className="table-light">
							<tr>
								<th>Description</th>
								<th>Quantity Returned</th>
								<th>Amount</th>
								<th>Return Date</th>
							</tr>
						</thead>
						<tbody>
							{returnedItemsWithCode.map((item, idx) => (
								<tr key={idx}>
									<td>{`(${item.itemCode}) ${item.description}`}</td>
									<td>{item.quantity}</td>
									<td>
										₹
										{(item.amount * item.quantity).toFixed(
											2
										)}
									</td>
									<td>{formatDate(item.returnDate)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Divider sx={{ marginY: 2 }} />
			<Box sx={{ fontWeight: "bold" }}>
				<Typography>
					Total Returned Price : ₹ {totalAmountOfReturnedItems}
				</Typography>
				<Typography>
					Eligible Refund : ₹
					{remainingPayment < 0
						? (0 - remainingPayment).toFixed(2)
						: 0}
				</Typography>
			</Box>
		</Paper>
	);
};
